// A doc-control sheet as something you can put in an <img> or on a
// texture.
//
// Sheets are GZIPPED PDFs, so a sheet's link in an <img> renders nothing
// at all. This inflates it and rasterises page one; a plain image link is
// used as-is. Shared by the 2D layout and the model stage so the drawing
// under the overlay and the drawing under the model are the same picture
// at the same scale - two implementations of "get the sheet" would drift,
// and the alignment saved against one would be wrong for the other.
import { useEffect, useState } from "react";
import { url } from "api";
import { renderPdfThumbnail } from "helpers/pdfPreview";

const abs = (u) => (!u ? "" : /^(https?:|data:|blob:)/i.test(u) ? u : `${url}${u.startsWith("/") ? "" : "/"}${u}`);
const IS_RASTER = /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i;
const IS_PDF = /\.pdf(\.gz)?(\?|$)/i;

export default function useSheetImage(sheet, { scale = 1.6, maxWidth = 1600, quality = 0.8, page = 1 } = {}) {
  const [src, setSrc] = useState("");
  const link = sheet && sheet.link;
  // A DWG is drawn from the page images rendered at upload (docControl.pages).
  const rendered = sheet && Array.isArray(sheet.pages) && !IS_PDF.test(String(link || "")) && !IS_RASTER.test(String(link || ""))
    ? (sheet.pages[page - 1] || sheet.pages[0] || "") : "";
  useEffect(() => {
    let alive = true;
    setSrc("");
    if (rendered) { setSrc(abs(rendered)); return undefined; }
    if (!link) return undefined;
    if (IS_RASTER.test(String(link))) { setSrc(abs(link)); return undefined; }
    renderPdfThumbnail(link, { scale, maxWidth, quality, page })
      .then((d) => { if (alive && d) setSrc(d); })
      .catch(() => { /* no backdrop is survivable - the overlay still draws */ });
    return () => { alive = false; };
  }, [link, rendered, scale, maxWidth, quality, page]);
  return src;
}
